import { RefObject } from 'react';
import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import {
    belgianPostalCodeLength,
    frenchPostalCodeLength,
    germanPostalCodeLength,
} from '@aviv-estimation-funnel/packages/libs/portals-configurations/src/lib/constants';
import { extractAutocompleteData, filterCityResults } from '@mfe-helpers/location/extractor';
import { autocompleteAPI, geoCodingAPI } from '@mfe-services/geoApiRequest';
import type { AutocompleteData } from '@mfe-types/geo';
import {
    AddressLevel,
    AutocompleteParams,
    AutocompleteRequestPayload,
    GeocodingParams,
    GeoCountry,
    GermanyAutocompleteRequestPayload,
    PLACE_TYPES_LIST,
    PlaceTypes,
    TAddressLevel,
    TPlaceTypes,
} from '@mfe-types/geo';

export const AVIV_GEO_RESULT_LIMIT = 5;

export const getPostalCodeLength = (portal: Portal): number => {
    switch (portal) {
        case Portal.ImmoWeb:
            return belgianPostalCodeLength;
        case Portal.SeLoger:
        case Portal.LogicImmo:
            return frenchPostalCodeLength;
        case Portal.ImmoWelt:
        case Portal.Immonet:
        default:
            return germanPostalCodeLength;
    }
};

export const focusOnNextField = (
    values: Record<string, unknown> | Array<Record<string, unknown>>,
    postalCodeRef: RefObject<HTMLInputElement>,
    houseNumberRef: RefObject<HTMLInputElement>,
): void => {
    const fieldNamesArray = ['postalCode', 'cityName', 'streetName', 'houseNumber'];
    const fieldValues = Object.entries(values);

    const firstEmptyFieldIndex = fieldValues.find(
        (value) => value[1] === '' && fieldNamesArray.includes(value[0]),
    );

    if (firstEmptyFieldIndex) {
        const [keyName] = firstEmptyFieldIndex;
        switch (keyName) {
            case 'streetName': {
                const streetNameInput = document.getElementsByName('streetName')[0];
                if (streetNameInput) {
                    streetNameInput.focus();
                }
                break;
            }
            case 'cityName': {
                const cityNameInput = document.getElementById('cityName');
                if (cityNameInput) {
                    cityNameInput.focus();
                }
                break;
            }
            case 'postalCode':
                postalCodeRef?.current?.focus();
                break;
            case 'houseNumber':
                houseNumberRef?.current?.focus();
                break;
        }
    }
};

export const getPortalCountry = (portal: Portal): GeoCountry | undefined => {
    switch (portal) {
        case Portal.ImmoWeb:
            return GeoCountry.Belgium;
        case Portal.ImmoWelt:
        case Portal.Immonet:
            return GeoCountry.Germany;
        case Portal.SeLoger:
        case Portal.LogicImmo:
            return GeoCountry.France;
        default:
            throw new Error(`Portal ${portal} is not supported`);
    }
};

export const getPlaceTypes = (addressLevel?: TAddressLevel): TPlaceTypes[] | undefined => {
    switch (addressLevel) {
        case AddressLevel.City:
            return [PlaceTypes.Municipality, PlaceTypes.Borough];
        case AddressLevel.Street:
            return [PlaceTypes.Street];
        case AddressLevel.HouseNumber:
            return [PlaceTypes.HouseNumber];
        default:
            throw new Error(`Address level ${addressLevel} is not supported`);
    }
};

export const getParentTypeById = (parentId: string): Array<TPlaceTypes> | undefined => {
    const parentType = parentId.slice(0, 4);
    if (PLACE_TYPES_LIST.includes(parentType as TPlaceTypes)) {
        return [parentType] as Array<TPlaceTypes>;
    }
    return undefined;
};

export const getParentTypeByAddressLevel = (addressLevel: TAddressLevel): Array<TPlaceTypes> => {
    switch (addressLevel) {
        case AddressLevel.City:
            return [];
        case AddressLevel.Street:
            return [PlaceTypes.Municipality, PlaceTypes.Borough];
        case AddressLevel.HouseNumber:
            return [PlaceTypes.Street, PlaceTypes.Municipality, PlaceTypes.Borough];
        default:
            throw new Error(`Address level ${addressLevel} is not supported`);
    }
};

export const getAutoCompleteRequestPayload = ({
    text,
    portal,
    addressLevel,
    parentId,
    limit,
}: AutocompleteRequestPayload): AutocompleteParams => {
    const portalCountry = getPortalCountry(portal);
    const placeTypes = getPlaceTypes(addressLevel);
    const parentTypes = parentId
        ? getParentTypeById(parentId)
        : getParentTypeByAddressLevel(addressLevel);

    return {
        text,
        limit,
        ...(portalCountry && { countries: [portalCountry] }),
        ...(placeTypes?.length && { place_types: placeTypes }),
        ...(parentTypes?.length && { parent_types: parentTypes }),
        ...(parentId && { parent_ids: [parentId] }),
    };
};

export const fetchAutocompleteAPI = async ({
    text,
    portal,
    addressLevel,
    limit = AVIV_GEO_RESULT_LIMIT,
    parentId,
    isGermanPortal,
    locale,
}: GermanyAutocompleteRequestPayload): Promise<Array<AutocompleteData>> => {
    const params = getAutoCompleteRequestPayload({ text, portal, addressLevel, limit, parentId });

    // TODO https://avivgroup.atlassian.net/browse/VEGA-1242
    // remove filter when geoApi is ready for DE portals
    if (isGermanPortal) {
        if (params?.parent_types?.length) {
            params.parent_types = params.parent_types.filter((type) => type !== 'AD09');
        }
        if (params.place_types && params.place_types.length > 0) {
            params.place_types = params.place_types.filter((type) => type !== 'AD09');
        }
    }

    let { items } = await autocompleteAPI(params);

    if (addressLevel === AddressLevel.City) {
        items = filterCityResults(items, text);
    }
    return extractAutocompleteData(items, locale);
};

export const fetchGeocodingAPI = async (params: GeocodingParams, streetId: string) => {
    const { items } = await geoCodingAPI(params);
    // Other available providers than aviv are google and mapbox
    const geoMatchPlaces = items?.aviv;
    const houseNumberGeoMatchPlace = geoMatchPlaces?.find(
        (item) => item.match.type_key === PlaceTypes.HouseNumber && item.match.id,
    );
    if (houseNumberGeoMatchPlace) {
        const streetParent = houseNumberGeoMatchPlace.match.parents.find(
            (item) => item.type_key === PlaceTypes.Street,
        );
        if (streetParent?.id === streetId) return houseNumberGeoMatchPlace;
    }
    return;
};
